/* eslint-disable no-console */
import {FileOutlined} from '@ant-design/icons';
import {Typography, Flex, Divider, Checkbox, Row, Col, Card, Table} from 'antd';
import {ColumnsType} from 'antd/es/table';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {pdfjs} from 'react-pdf';
import {useSearchParams} from 'react-router-dom';

import BRSR_logo from '../../assets/BRSR.png';
import CA_STRATEGY_logo from '../../assets/CA_STRATEGY_logo.png';
import CDP_logo from '../../assets/cdp.png';
import facilityImage from '../../assets/facilityImage.png';
import GRI_logo from '../../assets/GRI.png';
import TCFD_logo from '../../assets/TCFDIcon.png';
import BlockAccessPage from '../../components/BlockAccess/BlockAccessPage';
import PilComp from '../../components/DisplayComponent/PilComp';
import Loader from '../../components/Loader/Loader';
import PageContent from '../../components/PageContent/PageContent';
import {defaultCol} from '../../components/Table/Interface/ITable';
import reportStudio from '../../services/modules/ReportStudio/reportStudio';
import {getUniqueArray} from '../../shared/commonFunctions';
import Year from '../../shared/Year';
import {accessModule} from '../../store/companyStore/companyDetailsStore';
import {checkCurrentYearOrBelow} from '../../utils/commonFunctions';
// import {yearSelect} from '../../utils/FormUtils/yearSelect';
import {shouldBlockModuleAccess} from '../../utils/SubscriptionHelper';

import {
  ESGCategories,
  ReportTypesEnumKey,
  ReportTypesEnumLabel
} from './constants/commonConstants';
import {
  ReportStudioKey,
  ReportStudioLabel
} from './constants/ReportStudioKeyLabel';
import ReportStudioDrawer from './ReportStudioDrawer';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const {Title, Text} = Typography;

const ReportStudio: React.FC = () => {
  const [searchParam, setSearchParams] = useSearchParams();
  const [selectedYear, setSelectedYear] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [selectedReport, setSelectedReport] = useState<any>({});
  const [dataSource, setDataSource] = useState<any>([]);
  const [reportData, setReportData] = useState<any>([]);
  const [filteredReports, setFilteredReports] = useState<any>(reportData);
  const [selectedCategories, setSelectedCategories] = useState<string[]>();
  const [arrayOptions, setArrayOptions] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [pdfThumbnails, setPdfThumbnails] = useState<Record<string, string>>(
    {}
  );

  const renderThumbnail = (
    reportCategory: string,
    thumbnail: string,
    docExt: string
  ) => {
    let finalThumbnail = '';
    switch (reportCategory) {
      case ReportTypesEnumKey.CA:
      case ReportTypesEnumKey.STRATEGY:
        finalThumbnail = thumbnail ?? CA_STRATEGY_logo;
        break;
      case ReportTypesEnumKey.GRI:
        finalThumbnail = thumbnail ?? GRI_logo;
        break;
      case ReportTypesEnumKey.BRSR:
        finalThumbnail = thumbnail
          ? docExt === 'pdf'
            ? thumbnail
            : BRSR_logo
          : BRSR_logo;
        break;
      case ReportTypesEnumKey.TCFD:
        finalThumbnail = thumbnail ?? TCFD_logo;
        break;
      case ReportTypesEnumKey.CDP:
        finalThumbnail = thumbnail ?? CDP_logo;
        break;
      default:
        finalThumbnail = thumbnail ?? '';
    }

    return finalThumbnail;
  };

  // const firstYear = yearSelect();

  useEffect(() => {
    const currentYear = checkCurrentYearOrBelow(
      searchParam.get('selectedYear')
    );
    setSelectedYear(currentYear);
    searchParam.set('selectedYear', currentYear);
    setSearchParams(searchParam);
  }, []);

  useEffect(() => {
    setDataSource(getReportVersions(selectedReport?.title));
  }, [isDrawerOpen]);

  useEffect(() => {
    if (selectedYear) {
      setIsLoading(true);
      reportStudio(selectedYear)
        .then((data) => {
          setReportData(data?.reportVersions);
          setFilteredReports(data?.reportVersions);
          setSelectedCategories(
            Array.from(
              new Set(
                data?.reportVersions?.map((item: any) =>
                  ESGCategories?.includes(item?.doc_type)
                    ? ReportTypesEnumKey.ESG_REPORTS
                    : item?.doc_type
                )
              )
            )
          );
          setArrayOptions(
            getUniqueArray(
              data?.reportVersions?.map((item: any) => {
                const isESG = ESGCategories?.includes(item?.doc_type);

                return isESG
                  ? {
                      value: ReportTypesEnumKey.ESG_REPORTS,
                      label: ReportTypesEnumLabel.ESG_REPORTS
                    }
                  : {
                      value: item?.doc_type,
                      label: item?.title
                    };
              }),
              'value'
            )
          );
        })
        .catch(() => {
          setReportData([]);
          setFilteredReports([]);
          setSelectedCategories([]);
          setArrayOptions([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
    const loadData = async () => {
      if (selectedYear) {
        setIsLoading(true);

        if (reportData?.length !== 0) {
          await generatePdfThumbnails(reportData);

          const fallbackTimer = setTimeout(() => {}, 2);

          return () => {
            clearTimeout(fallbackTimer);
          };
        }
      }
    };

    void loadData();
  }, [selectedYear]);

  const generatePdfThumbnails = async (mainData: any) => {
    const newThumbnails: Record<string, string> = {};

    if (mainData) {
      for (const reportVersion of mainData) {
        if (
          reportVersion.generatedReports &&
          reportVersion.generatedReports?.length > 0
        ) {
          const latestReport = reportVersion?.generatedReports[0]; // Get the latest report
          const docKey = reportVersion?.doc_type;
          if (latestReport.url && latestReport.doc_ext === 'pdf') {
            try {
              const thumbnail = await generatePdfThumbnail(latestReport.url);
              newThumbnails[docKey] = thumbnail;
            } catch (error) {
              console.error(
                `Failed to generate thumbnail for ${reportVersion.title}:`,
                error
              );
              newThumbnails[docKey] = facilityImage;
            }
          } else {
            newThumbnails[docKey] = facilityImage;
          }
        }
      }
    }

    setPdfThumbnails(newThumbnails);
  };

  const generatePdfThumbnail = async (pdfUrl: string): Promise<string> => {
    try {
      const pdf = await pdfjs.getDocument(pdfUrl).promise;

      const page = await pdf.getPage(1);

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      const viewport = page.getViewport({scale: 1});

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      if (!context) {
        throw new Error('Could not get canvas context');
      }

      const renderContext = {
        canvasContext: context,
        viewport
      };

      await page.render(renderContext).promise;

      return canvas.toDataURL('image/jpeg', 0.8);
    } catch (error) {
      console.error('Error generating PDF thumbnail:', error);
      throw error;
    }
  };

  const columns: Array<defaultCol & ColumnsType<any>[0]> = [
    {
      title: ReportStudioLabel.DATE,
      dataIndex: ReportStudioKey.DATE,
      key: ReportStudioKey.DATE,
      render: (value) => {
        return moment(value)?.format('DD/MM/YYYY');
      }
    },
    {
      title: ReportStudioLabel.FILE_NAME,
      dataIndex: ReportStudioKey.FILE_NAME,
      key: ReportStudioKey.FILE_NAME,
      render: () => {
        return findReportTitle(selectedReport?.title);
      }
    },
    {
      title: ReportStudioLabel.DOCUMENT_TYPE,
      dataIndex: ReportStudioKey.DOCUMENT_TYPE,
      key: ReportStudioKey.DOCUMENT_TYPE,
      render: (docExt: string) => (
        <Text style={{textTransform: 'uppercase'}}>{docExt}</Text>
      )
    },
    {
      title: ReportStudioLabel.ACTIONS,
      key: ReportStudioKey.ACTIONS,
      render: (_text: string, record: any) => {
        return (
          <a
            href={record?.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-report-studio-preview block"
            style={{pointerEvents: 'auto', cursor: 'pointer'}}
          >
            <PilComp
              color="#719A5D"
              value="Preview & Download"
              style={{
                maxWidth: '150px',
                width: '130px',
                color: '#E1F3D9',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            />
          </a>
        );
      }
    }
  ];

  const getReportVersions = (reportTitle: string) => {
    const reportAllVersions = filteredReports?.find(
      (item: any) => item.title === reportTitle
    )?.generatedReports;

    return reportAllVersions;
  };

  const getLatestReportUrl = (reportTitle: string): string => {
    if (!reportTitle) {
      return '';
    }

    const report = reportData?.find((r: any) => r.title === reportTitle);
    if (!report?.generatedReports || report?.generatedReports?.length === 0) {
      return '';
    }

    if (reportTitle === ReportTypesEnumLabel.STRATEGY) {
      const strategyReport = reportData?.find(
        (version: any) => version.doc_type === ReportTypesEnumKey.STRATEGY
      );

      const url = strategyReport?.generatedReports[0]?.url || '';

      return url;
    } else {
      const url = report?.generatedReports?.[0]?.url || '';

      return url;
    }
  };

  const findReportTitle = (reportTitle: string) => {
    return reportTitle === ReportTypesEnumLabel.STRATEGY
      ? reportTitle
      : `${reportTitle} ${selectedYear}`;
  };

  return (
    <PageContent>
      <Flex vertical className="report-studio">
        <Flex align="center" justify="space-between">
          <Title level={4} className="mainHeader">
            Report Studio
          </Title>
          {shouldBlockModuleAccess(accessModule.reportStudio) ? null : (
            <>
              {selectedYear && (
                <Year
                  setSelectedYear={setSelectedYear}
                  isESG
                  noBottomMargin
                  colSpan={24}
                />
              )}
            </>
          )}
        </Flex>

        <Divider />
        {shouldBlockModuleAccess(accessModule.reportStudio) ? (
          <Flex
            style={{
              width: '100%',
              backgroundColor: '#ffffff',
              border: '1px solid #F3F4F8',
              borderRadius: '12px',
              padding: '20px'
            }}
            gap={45}
          >
            <Flex
              vertical
              gap={25}
              style={{width: '100%', marginTop: 100, position: 'relative'}}
            >
              <BlockAccessPage />
            </Flex>
          </Flex>
        ) : (
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <>
                <Flex
                  className="report-studio-checkboxes-group"
                  align="center"
                  justify="flex-end"
                >
                  <Checkbox.Group
                    options={arrayOptions}
                    value={selectedCategories}
                    onChange={(selectedValues: string[]) => {
                      setSelectedCategories(selectedValues);
                      const filtered = reportData?.filter((item: any) => {
                        const category = ESGCategories?.includes(item?.doc_type)
                          ? ReportTypesEnumKey.ESG_REPORTS
                          : item.doc_type;

                        return selectedValues?.includes(category);
                      });
                      setFilteredReports(filtered);
                    }}
                  />
                </Flex>
                <Row className="report-studio-container" gutter={[36, 24]}>
                  {filteredReports?.some(
                    (item: any) => item?.generatedReports?.length > 0
                  ) ? (
                    (filteredReports || reportData)?.map(
                      (report: any, index: number) => {
                        const thumbnailKey = `${report.title}-${selectedYear}`;
                        const hasPdfThumbnail = pdfThumbnails[thumbnailKey];

                        const latestReport = reportData?.find(
                          (r: any) => r.title === report.title
                        )?.generatedReports?.[0];

                        return (
                          report?.generatedReports?.length > 0 && (
                            <Col
                              span={6}
                              key={index}
                              style={{
                                borderRadius: '16px 16px 0 0',
                                height: '500px'
                              }}
                            >
                              <Flex
                                className="reportStudioCard"
                                style={{
                                  backgroundColor: '#F3F4F8',
                                  borderRadius: '16px 16px 0 0'
                                }}
                                vertical
                              >
                                <Flex
                                  className="report-studio-thumbnail"
                                  justify="center"
                                >
                                  <img
                                    alt="PDF preview"
                                    src={renderThumbnail(
                                      report?.doc_type,
                                      hasPdfThumbnail,
                                      latestReport?.doc_ext
                                    )}
                                    style={
                                      hasPdfThumbnail
                                        ? {
                                            aspectRatio: '1',
                                            maxWidth: '100%'
                                          }
                                        : {
                                            aspectRatio: '1',
                                            margin: '20px',
                                            maxWidth: '100%'
                                          }
                                    }
                                  />
                                </Flex>
                                <Flex
                                  vertical
                                  align="flex-start"
                                  justify="center"
                                  style={{margin: '20px'}}
                                >
                                  <div className="report-studio-info-text">
                                    Last updated:
                                    {` ${moment(
                                      report?.generatedReports[0]?.createdAt
                                    ).format('DD/MM/YYYY')}`}
                                  </div>
                                  <div className="report-studio-item-title">
                                    {`${report.title} ${selectedYear}`}
                                  </div>
                                  <a
                                    href={getLatestReportUrl(report.title)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-report-studio-preview block"
                                    onClick={(e) => {
                                      const url = getLatestReportUrl(
                                        report.title
                                      );
                                      if (!url) {
                                        e.preventDefault();
                                      }
                                    }}
                                  >
                                    <FileOutlined
                                      style={{marginRight: '8px'}}
                                    />
                                    Preview &amp; Download
                                  </a>
                                  <div
                                    onClick={() => {
                                      setSelectedReport(report);
                                      setIsDrawerOpen(true);
                                    }}
                                    style={{
                                      font: 'normal 700 14px Montserrat',
                                      alignSelf: 'center',
                                      textDecoration: 'underline',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    See all versions
                                  </div>
                                </Flex>
                              </Flex>
                            </Col>
                          )
                        );
                      }
                    )
                  ) : (
                    <Col span={24}>
                      <Card style={{textAlign: 'center', padding: '40px'}}>
                        <Text style={{fontSize: '16px', color: '#666'}}>
                          No data
                        </Text>
                      </Card>
                    </Col>
                  )}
                </Row>
              </>
            )}
            <ReportStudioDrawer
              open={isDrawerOpen}
              onCancel={() => {
                setIsDrawerOpen(false);
              }}
              stepTitle={findReportTitle(selectedReport?.title)}
              ChildComponent={
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                />
              }
            />
          </>
        )}
      </Flex>
    </PageContent>
  );
};

export default ReportStudio;
